import React, { useState, useEffect } from 'react';
import { Calendar, ChevronDown, ChevronRight, Edit2, Trash2, AlertCircle, Clock, Users, X, Camera } from 'lucide-react';
import SessionForm from './SessionForm';

const ResultsMaintenance = () => {
  const [sessions, setSessions] = useState([]);
  const [games, setGames] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSeasons, setExpandedSeasons] = useState({});
  const [editingSession, setEditingSession] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [fullscreenImage, setFullscreenImage] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sessionsRes, gamesRes, usersRes] = await Promise.all([
        fetch('/api/sessions/all').then(r => r.json()),
        fetch('/api/games').then(r => r.json()),
        fetch('/api/users').then(r => r.json())
      ]);
      setSessions(sessionsRes);
      setGames(gamesRes);
      setUsers(usersRes);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group sessions by season (Aug 1 - Jun 30)
  const groupSessionsBySeason = () => {
    const groups = {};
    sessions.forEach(session => {
      const date = new Date(session.date);
      const year = date.getFullYear();
      const month = date.getMonth();
      
      let seasonKey;
      if (month >= 7) { // Aug-Dec
        seasonKey = `${year}-${year + 1}`;
      } else { // Jan-Jul
        seasonKey = `${year - 1}-${year}`;
      }
      
      if (!groups[seasonKey]) {
        groups[seasonKey] = [];
      }
      groups[seasonKey].push(session);
    });
    return groups;
  };

  const toggleSeason = (season) => {
    setExpandedSeasons(prev => ({
      ...prev,
      [season]: !prev[season]
    }));
  };

  // Fetch full session data (including results) before editing
  const handleEditClick = async (session) => {
    try {
      const res = await fetch(`/api/sessions/${session.id}`);
      if (!res.ok) throw new Error('Failed to fetch session details');
      const fullSession = await res.json();
      setEditingSession(fullSession);
    } catch (err) {
      console.error('Error fetching session:', err);
      alert('Virhe ladattaessa session tietoja');
    }
  };

  const handleDelete = async (sessionId) => {
    try {
      const res = await fetch(`/api/sessions/${sessionId}`, { method: 'DELETE' });
      if (res.ok) {
        setSessions(sessions.filter(s => s.id !== sessionId));
        setDeleteConfirm(null);
      } else {
        const error = await res.json();
        alert(`Virhe: ${error.error}`);
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Poisto epäonnistui');
    }
  };

  const handleEditSave = async (sessionData) => {
    // TODO: Implement session update API call
    try {
      const res = await fetch(`/api/sessions/${editingSession.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sessionData)
      });
      if (res.ok) {
        loadData(); // Refresh data
        setEditingSession(null);
      } else {
        const error = await res.json();
        alert(`Virhe: ${error.error}`);
      }
    } catch (err) {
      console.error('Update error:', err);
      alert('Päivitys epäonnistui');
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}min` : `${mins}min`;
  };

  const getGameName = (gameId) => {
    const game = games.find(g => g.id === gameId);
    return game?.name || 'Tuntematon';
  };

  const groupedSessions = groupSessionsBySeason();
  const seasonKeys = Object.keys(groupedSessions).sort().reverse();

  if (loading) return <div className="loading">Ladataan tuloksia...</div>;

  return (
    <div className="results-maintenance">
      <h2>Tulosten ylläpito</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem' }}>
        Hallinnoi pelisessioita kausittain. Voit muokata tai poistaa tuloksia.
      </p>

      {/* Edit Modal */}
      {editingSession && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close-btn" onClick={() => setEditingSession(null)}><X /></button>
            <h3>Muokkaa sessiota</h3>
            <SessionForm 
              games={games}
              users={users}
              initialData={editingSession}
              onSave={handleEditSave}
              onCancel={() => setEditingSession(null)}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content card" style={{ maxWidth: '400px', textAlign: 'center' }}>
            <AlertCircle size={48} color="#e74c3c" style={{ marginBottom: '1rem' }} />
            <h3>Haluatko varmasti poistaa?</h3>
            <p style={{ color: '#666', margin: '1rem 0' }}>
              Tämä poistaa session ja kaikki siihen liittyvät tulokset pysyvästi.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Peruuta</button>
              <button 
                className="btn-primary" 
                style={{ background: '#e74c3c' }}
                onClick={() => handleDelete(deleteConfirm)}
              >
                Poista
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Viewer */}
      {fullscreenImage && (
        <div 
          className="modal-overlay" 
          onClick={() => setFullscreenImage(null)}
          style={{ cursor: 'pointer', zIndex: 3000 }}
        >
          <div 
            style={{ 
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="close-btn" 
              onClick={() => setFullscreenImage(null)}
              style={{
                position: 'absolute',
                top: '-40px',
                right: '0',
                background: 'rgba(255,255,255,0.9)',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
              }}
            >
              <X size={20} />
            </button>
            <img 
              src={fullscreenImage} 
              alt="Session" 
              style={{ 
                maxWidth: '90vw', 
                maxHeight: '85vh', 
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
              }} 
            />
          </div>
        </div>
      )}

      {/* Season List */}
      <div className="seasons-list">
        {seasonKeys.length === 0 ? (
          <p style={{ color: '#888' }}>Ei sessioita</p>
        ) : (
          seasonKeys.map(season => (
            <div key={season} className="season-group card">
              <div 
                className="season-header-row clickable-row"
                onClick={() => toggleSeason(season)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {expandedSeasons[season] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  <Calendar size={18} />
                  <strong>Kausi {season}</strong>
                </div>
                <span className="session-count">{groupedSessions[season].length} peliä</span>
              </div>

              {expandedSeasons[season] && (
                <div className="season-sessions">
                  <table className="results-table results-table-responsive">
                    <thead>
                      <tr>
                        <th>Päivä</th>
                        <th>Peli</th>
                        <th className="hide-mobile-3"><Users size={14} /></th>
                        <th className="hide-mobile-2">Pisteet</th>
                        <th className="hide-mobile-1"><Clock size={14} /></th>
                        <th>Toiminnot</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedSessions[season].map(session => (
                        <tr key={session.id}>
                          <td>{new Date(session.date).toLocaleDateString('fi-FI')}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {session.image_url ? (
                                <div 
                                  className="session-thumb" 
                                  title="Klikkaa avataksesi kuvan"
                                  onClick={(e) => { e.stopPropagation(); setFullscreenImage(session.image_url); }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <img 
                                    src={session.image_url} 
                                    alt="Session" 
                                    style={{ 
                                      width: '28px', 
                                      height: '28px', 
                                      borderRadius: '4px', 
                                      objectFit: 'cover',
                                      border: '1px solid #d35400'
                                    }} 
                                  />
                                </div>
                              ) : (
                                <span title="Ei kuvaa" style={{ cursor: 'default' }}>
                                  <Camera size={16} style={{ color: '#ccc' }} />
                                </span>
                              )}
                              <span>{getGameName(session.game_id)}</span>
                            </div>
                          </td>
                          <td className="hide-mobile-3">{session.player_count}</td>
                          <td className="hide-mobile-2">{session.totalRawScore || '-'}</td>
                          <td className="hide-mobile-1">{formatDuration(session.durationMinutes)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button 
                                className="btn-icon"
                                onClick={() => handleEditClick(session)}
                                title="Muokkaa"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button 
                                className="btn-icon btn-danger"
                                onClick={() => setDeleteConfirm(session.id)}
                                title="Poista"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ResultsMaintenance;

